<template>
  <nav id="main-navigation" class="navbar-shadow relative w-full bg-white p-4">
    <div class="flex items-center justify-between">
      <div class="flex items-center space-x-8">
        <component
          :is="isUserLoggedIn ? 'InertiaLink' : 'a'"
          :href="isUserLoggedIn ? '/dashboard' : '/'"
          prefetch="hover"
        >
          <img
            class="w-40 p-1"
            src="/growthmentor-logo.svg"
            alt=""
            data-allow-mismatch
          />
        </component>

        <nav class="hidden xl:block">
          <ul class="flex items-center">
            <li
              v-for="(item, index) in mainNavigation"
              :key="index"
              class="mr-2 text-sm font-semibold whitespace-nowrap"
              :class="[
                item.isInMoreButton
                  ? 'hidden min-[1270px]:inline-block'
                  : 'inline-block',
              ]"
            >
              <InertiaLink
                v-if="!item.dropdown"
                :href="item.href[accessControl.actorType]"
                class="inline-flex items-center rounded-full px-3 py-2 whitespace-nowrap text-neutral-900 transition-colors hover:bg-neutral-50 focus:outline-none focus-visible:ring focus-visible:ring-neutral-200"
                prefetch="hover"
              >
                <component :is="item.icon" class="mr-2 size-4.5" />
                {{ item.name }}
              </InertiaLink>

              <NavbarDropdown v-else :name="item.name" :items="item.dropdown">
                <template #icon>
                  <component :is="item.icon" class="mr-2 size-4.5 shrink-0" />
                </template>
              </NavbarDropdown>
            </li>
            <li
              v-if="mainNavigationItemsInMoreButton.length"
              class="text-sm min-[1270px]:hidden"
            >
              <NavbarDropdown
                name="More"
                :items="mainNavigationItemsInMoreButton"
              >
                <template #icon>
                  <DotsIcon class="mr-2 size-4.5 shrink-0" />
                </template>
              </NavbarDropdown>
            </li>
          </ul>
        </nav>
      </div>

      <div class="flex items-center">
        <div v-if="isUserLoggedIn" class="flex items-center space-x-4">
          <!-- Purple is the community action, blue stays for booking. -->
          <BaseButton
            v-if="ASK_ENABLED"
            size="small"
            color="purple"
            href="/ask/new"
            is-inertia-link
            class="hidden whitespace-nowrap md:block"
          >
            Ask
          </BaseButton>
          <BaseButton
            size="small"
            href="/search"
            is-inertia-link
            class="hidden whitespace-nowrap sm:block"
            :prefetch="['mount', 'hover']"
            cache-for="5m"
          >
            Browse mentors
          </BaseButton>
          <div class="flex items-center space-x-2">
            <NavbarIconItem
              :has-red-indicator="!!unreadMessages.length"
              :indicator-value="unreadMessages.length"
              screen-reader-text="View messages"
              href="/conversations"
              @click="markNotificationsAsRead('messages')"
              test_id="messages-icon"
            >
              <MessageIcon class="size-6 xl:size-4.5" />
            </NavbarIconItem>
            <NavbarWhatsNewButton />
            <NavbarNotificationsButton />
          </div>

          <NavbarUserDropdown
            :navigation="dropdownNavigation"
            class="hidden xl:inline-flex"
          />
        </div>

        <div v-else class="hidden items-center space-x-2 xl:flex">
          <BaseButton size="small" href="/search">Browse mentors</BaseButton>
          <BaseButton
            size="small"
            color="outline-secondary"
            href="/mentees/sign_up?flow=classic"
          >
            Sign up
          </BaseButton>
          <BaseButton size="small" color="primary" href="/users/sign_in">
            Log in
          </BaseButton>
        </div>

        <NavbarHamburgerButton
          class="p-4 xl:hidden"
          :is-open="isMobileNavigationOpen"
          @click="toggleMobileNavigation"
        />
      </div>
    </div>

    <MobileNavbar
      :is-open="isMobileNavigationOpen"
      :navigation="mainNavigation"
      :user-dropdown-navigation="dropdownNavigation"
    />
  </nav>
</template>

<script setup>
import BaseButton from '../BaseButton.vue'
import NavbarIconItem from './NavbarIconItem.vue'
import NavbarUserDropdown from './NavbarUserDropdown.vue'
import NavbarHamburgerButton from './NavbarHamburgerButton.vue'
import NavbarDropdown from './NavbarDropdown.vue'
import MessageIcon from '~icons/lucide/message-square-text'
import DotsIcon from '~icons/lucide/more-horizontal'
import MobileNavbar from './MobileNavbar.vue'
import { router } from '@inertiajs/vue3'
import NavbarNotificationsButton from '@/shared/components/navbar/NavbarNotificationsButton.vue'
import NavbarWhatsNewButton from '@/shared/components/navbar/NavbarWhatsNewButton.vue'
import { PERSONAL_MESSAGE_NOTIFICATIONS_CHANNEL } from '@/shared/data/actionCableChannels.js'
import { onMounted, ref } from 'vue'
import { useNavigationElements } from '@/shared/composables/navigation/useNavigationElements.js'
import { useActionCable } from '@/shared/composables/useActionCable.ts'
import { useDebugger } from '@/shared/composables/useDebugger.js'
import axios from 'axios'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem'
import { useFeatureFlags } from '@/shared/composables/useFeatureFlags'

const { $debug } = useDebugger()
const { isUserLoggedIn, accessControl } = usePermissionSystem()
const { ASK_ENABLED } = useFeatureFlags()
const { mainNavigation, mainNavigationItemsInMoreButton, dropdownNavigation } =
  useNavigationElements()
const { subscribeChannel } = useActionCable({
  channelName: PERSONAL_MESSAGE_NOTIFICATIONS_CHANNEL,
  onConnected: () => {
    $debug(`[AC Channel: ${PERSONAL_MESSAGE_NOTIFICATIONS_CHANNEL}] connected`)
  },
  onReceived: (data) => {
    $debug(
      `[AC Channel: ${PERSONAL_MESSAGE_NOTIFICATIONS_CHANNEL}] data received`,
    )
    const notifications = JSON.parse(data.notifications)
    unreadMessages.value = notifications.filter(
      (notification) => !notification.read_at,
    )
  },
})

const isMobileNavigationOpen = ref(false)
const unreadMessages = ref([])

const toggleMobileNavigation = () => {
  isMobileNavigationOpen.value = !isMobileNavigationOpen.value
}

const markNotificationsAsRead = (notificationsType) => {
  axios.post('/notifications/mark_all_as_read_by_type', {
    type: notificationsType,
  })
  unreadMessages.value = []
}

onMounted(() => {
  // Hide navigation when user changes page
  router.on('success', () => {
    isMobileNavigationOpen.value = false
  })

  subscribeChannel()
})
</script>
